import React, { useCallback, useEffect, useRef, useState } from 'react'
import pa from '../ProductActivityPage.module.css'
import styles from './Pallets.module.css'
import lc from './List.module.css'
import { ExportIcon } from './chrome'
import { apiJson } from '../../lib/api'
import { workbook, XLSX_TYPE } from '../batches/xlsx'
import { fileTask, useTasks } from '../../components/TaskCentre'
import { useHelpPref } from '../../lib/preferences'
import { hasCapability, useAccess } from '../../lib/access'
import { PalletDrawer } from './PalletDrawer'
import { NoteBubble } from './NoteBubble'
import { CreatePalletsPanel } from './CreatePalletsPanel'
import { dropUrlParams, oneOf, urlParam } from './stock'
import {
  HOLD_CHIP,
  PSTATE,
  STATE_FILTERS,
  boxesText,
  filterPallets,
  lidText,
  lidWrong,
  plural,
  srcShort,
  srcTitle,
  type Pallet,
  type StateFilter,
  type ToPalletiseRow,
} from './model'
import { openableRow } from '../../lib/openableRow'

// The Pallets register and the To palletise work queue (V4 Stock → Pallets).
// Both read the API's settled figures; nothing here computes a stock number.

export type PalletsTab = 'register' | 'to'

const chipStyle = (t: [string, string, string]) => ({ background: t[1], color: t[2] })

export function PalletsView({ user, tab }: { user: any; tab: PalletsTab }) {
  const mayManage = hasCapability(useAccess(), 'manage_stock_dispatch')
  const [pallets, setPallets] = useState<Pallet[] | null>(null)
  const [toRows, setToRows] = useState<ToPalletiseRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [q, setQ] = useState('')
  const [state, setState] = useState<StateFilter>(() => (tab === 'register' ? oneOf(urlParam('state'), STATE_FILTERS.map(([k]) => k), '') : ''))
  const [openId, setOpenId] = useState<string | null>(() => (tab === 'register' ? urlParam('open') : null))
  const [createFor, setCreateFor] = useState<string | null>(null)
  const [toast, setToast] = useState('')

  // A deep link from the Overview (handoff §7n.1a) sets where this starts.
  useEffect(() => dropUrlParams(['state', 'open']), [])
  const [regHelp, toggleRegHelp] = useHelpPref(user, 'pallets')
  const [toHelp, toggleToHelp] = useHelpPref(user, 'to_palletise')
  const { startTask } = useTasks()

  const userRef = useRef(user)
  userRef.current = user
  const load = useCallback(async () => {
    try {
      if (tab === 'register') {
        const r = await apiJson<{ pallets: Pallet[] }>(userRef.current, '/api/pallets')
        setPallets(r.pallets)
      } else {
        const r = await apiJson<{ rows: ToPalletiseRow[] }>(userRef.current, '/api/pallets/to-palletise')
        setToRows(r.rows)
      }
      setError(null)
    } catch (e: any) {
      setError(`Failed to fetch ${tab === 'register' ? 'pallets' : 'the To palletise list'}: ${e?.message || e}`)
    }
  }, [tab])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(''), 3400)
    return () => clearTimeout(t)
  }, [toast])

  const shown = filterPallets(pallets || [], state, q)
  const helpOn = tab === 'register' ? regHelp : toHelp
  const toggleHelp = tab === 'register' ? toggleRegHelp : toggleToHelp

  const exportRows = () => {
    if (tab === 'register') {
      const rows = [
        ['Pallet', 'Variety', 'Box type', 'Site', 'Contractor', 'Harvest', 'Planned', 'Confirmed', 'Capacity', 'State', 'On hold', 'Order', 'Applied lid', 'Required lid', 'Location', 'Load'],
        ...shown.map((p) => [
          p.pallet_id,
          p.source.variety,
          p.source.box_type,
          p.source.site_name,
          p.source.contractor_name,
          p.source.harvest_date,
          p.planned_boxes,
          p.confirmed_boxes,
          p.capacity,
          PSTATE[p.state][0],
          p.hold ? p.hold.reason : '',
          p.order?.reference || '',
          p.applied_lid?.name || '',
          p.required_lid?.name || '',
          p.location?.name || '',
          p.dispatch?.reference || '',
        ]),
      ]
      startTask(fileTask('Export pallets', plural(shown.length, 'pallet') + ' as shown', 'pallets.xlsx', XLSX_TYPE, () => workbook([{ name: 'Pallets', rows }])))
    } else {
      const list = toRows || []
      const rows = [
        ['Source', 'Variety', 'Box type', 'Site', 'Contractor', 'Harvest', 'Boxes remaining', 'Capacity', 'Equivalent pallets', 'Orders'],
        ...list.map((r) => [
          r.source.id,
          r.source.variety,
          r.source.box_type,
          r.source.site_name,
          r.source.contractor_name,
          r.source.harvest_date,
          r.remaining,
          r.capacity,
          r.equivalent,
          r.orders.join(', '),
        ]),
      ]
      const boxes = list.reduce((a, r) => a + r.remaining, 0)
      const note = plural(list.length, 'source group') + ' · ' + boxes + ' boxes to palletise'
      startTask(fileTask('Export to palletise', note, 'to-palletise.xlsx', XLSX_TYPE, () => workbook([{ name: 'To palletise', rows }])))
    }
  }

  return (
    <div className={styles.wrap}>
      <div className={lc.toolbar}>
        {tab === 'register' && (
          <>
            <input
              className={`${lc.search} ${lc.searchNarrow}`}
              placeholder="Pallet ID, order, source…"
              aria-label="Search pallets"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <select className={lc.select} aria-label="Pallet state" value={state} onChange={(e) => setState(e.target.value as StateFilter)}>
              {STATE_FILTERS.map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </>
        )}
        <button type="button" className={lc.helpPill} onClick={toggleHelp} aria-expanded={helpOn}>
          {helpOn ? 'Hide help' : tab === 'register' ? 'How pallets work' : 'How this list is built'}
        </button>
        {tab === 'register' && pallets && <span className={lc.stats}>{plural(shown.length, 'pallet')}</span>}
        <button type="button" className={tab === 'register' && pallets ? lc.exportBtn : `${lc.exportBtn} ${lc.end}`} onClick={exportRows}>
          <ExportIcon />
          Export current results
        </button>
      </div>

      {error && (
        <div className={pa.errorBar} role="alert">
          {error}
        </div>
      )}

      {helpOn && tab === 'register' && <RegisterHelp onClose={toggleHelp} />}
      {helpOn && tab === 'to' && <ToHelp onClose={toggleHelp} />}

      {tab === 'register' ? (
        <div className={lc.card}>
          <div className={`${styles.regGrid} ${styles.head}`}>
            <span>Pallet</span>
            <span>Source</span>
            <span>Boxes</span>
            <span>Order</span>
            <span>State</span>
            <span>Lid applied / required</span>
            <span>Location</span>
            <span>Hold</span>
            <span>Load</span>
          </div>
          {pallets && shown.length === 0 && (
            <div className={lc.empty}>No pallets match. Pallets are created from Stock or from an order allocation.</div>
          )}
          {shown.map((p) => (
            <div
              key={p.id}
              className={`${styles.regGrid} ${styles.line} ${styles.clickable}`}
              {...openableRow(() => setOpenId(p.id))}
              data-testid="pallet-row"
            >
              <span className={styles.strong}>
                {p.pallet_id}
                {p.note && <NoteBubble notes={[{ text: p.note.text }]} />}
              </span>
              <span className={styles.stack}>
                <span className={styles.ellipsis}>{srcTitle(p.source)}</span>
                <span className={styles.sub}>{srcShort(p.source)}</span>
              </span>
              <span>{boxesText(p)}</span>
              <span className={styles.orderRef}>{p.order?.reference || 'Unassigned'}</span>
              <span>
                <span className={styles.chip} style={chipStyle(PSTATE[p.state])}>
                  {PSTATE[p.state][0]}
                </span>
              </span>
              <span className={lidWrong(p) ? styles.lidBad : undefined}>{lidText(p)}</span>
              <span className={styles.muted}>{p.location?.name || '—'}</span>
              <span>
                {p.hold && (
                  <span className={styles.chip} style={chipStyle(HOLD_CHIP)}>
                    {HOLD_CHIP[0]}
                  </span>
                )}
              </span>
              <span className={styles.muted}>{p.dispatch && p.dispatch.state !== 'cancelled' ? p.dispatch.reference : '—'}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className={lc.card}>
          <div className={`${styles.toGrid} ${styles.head}`}>
            <span>Eligible stock</span>
            <span>Order allocation</span>
            <span className={styles.right}>Boxes remaining</span>
            <span className={styles.right}>Capacity</span>
            <span>Equivalent</span>
            <span />
          </div>
          {toRows && toRows.length === 0 && <div className={lc.empty}>Everything eligible is already planned onto pallets.</div>}
          {(toRows || []).map((r) => (
            <div key={r.source.id} className={`${styles.toGrid} ${styles.line} ${lc.roomy}`} data-testid="to-row">
              <span className={styles.stack}>
                <span className={styles.strong}>{srcTitle(r.source)}</span>
                <span className={styles.sub}>{srcShort(r.source)}</span>
              </span>
              <span className={r.orders.length ? lc.link : lc.dim}>{r.orders.length ? r.orders.join(', ') : 'No order'}</span>
              <span className={`${styles.right} ${styles.strong}`}>{r.remaining}</span>
              <span className={styles.right}>{r.capacity}</span>
              <span className={lc.dim}>{plural(r.equivalent, 'pallet')} (equivalent)</span>
              <span style={{ textAlign: 'right' }}>
                {mayManage && (
                  <button type="button" className={lc.rowBtn} onClick={() => setCreateFor(r.source.id)}>
                    Create pallets
                  </button>
                )}
              </span>
            </div>
          ))}
        </div>
      )}

      {openId && (
        <PalletDrawer
          user={user}
          palletId={openId}
          onClose={() => setOpenId(null)}
          onChanged={(msg) => {
            if (msg) setToast(msg)
            load()
          }}
        />
      )}
      {createFor && (
        <CreatePalletsPanel
          user={user}
          sourceId={createFor}
          onClose={() => setCreateFor(null)}
          onCreated={(msg) => {
            setCreateFor(null)
            setToast(msg)
            load()
          }}
        />
      )}
      {toast && (
        <div className={pa.toast} role="status">
          {toast}
        </div>
      )}
    </div>
  )
}

function RegisterHelp({ onClose }: { onClose: () => void }) {
  return (
    <div className={lc.help} data-testid="pallets-help">
      <div className={lc.helpHead}>
        <span className={lc.helpTitle}>How pallets work</span>
        <span className={lc.helpLead}>A pallet is a planned intention until someone confirms what is physically on it.</span>
        <button type="button" className={lc.helpX} title="Hide help" aria-label="Hide help" onClick={onClose}>
          ×
        </button>
      </div>
      <div className={lc.helpItem}>
        <span>One row is one physical pallet</span>
        <p>
          It holds boxes from a single source group, so variety, box type and grower stay unmixed. Pallets are created from Stock or from an
          order allocation. Never typed in from nothing. A pallet holding fewer boxes than the source capacity is marked <strong>partial</strong>.
        </p>
      </div>
      <div className={lc.helpItem}>
        <span>Planned, confirmed, discrepancy</span>
        <div className={lc.defs}>
          <div>
            <strong>Planned</strong> = what the pallet is meant to hold
          </div>
          <div>
            <strong>Confirmed</strong> = what was counted on it
          </div>
          <div>
            <strong>Discrepancy</strong> = counted ≠ planned, and it stays there until someone resolves it
          </div>
          <div>The count is never silently overwritten.</div>
        </div>
      </div>
      <div className={lc.helpItem}>
        <span>Lid applied / required</span>
        <p>
          The required lid comes from the order line the pallet serves; the applied lid is what is on it. A mismatch shows red and blocks loading.
          The wrong lid is a customer rejection, not a paperwork detail. Unassigned pallets carry the source's own lid requirement instead.
        </p>
      </div>
      <div className={lc.helpItem}>
        <span>On a pallet is still in stock</span>
        <p>
          Boxes leave stock when a load departs, not when they are palletised. A pallet on hold cannot be loaded, and the hold travels with the
          boxes, so releasing it is a stock decision. Departed and cancelled pallets drop out of this register.
        </p>
      </div>
    </div>
  )
}

function ToHelp({ onClose }: { onClose: () => void }) {
  return (
    <div className={lc.help} data-testid="to-help">
      <div className={lc.helpHead}>
        <span className={lc.helpTitle}>How this list is built</span>
        <span className={lc.helpLead}>A work queue, not a stock view: what is available and not yet on a pallet.</span>
        <button type="button" className={lc.helpX} title="Hide help" aria-label="Hide help" onClick={onClose}>
          ×
        </button>
      </div>
      <div className={lc.helpItem}>
        <span>One row is one source group</span>
        <p>
          The same grouping as Stock. Harvest, site, contractor, variety, box type. Showing only groups with boxes still to palletise. A group
          disappears from the list once nothing is left to plan, so an empty list means the floor is caught up.
        </p>
      </div>
      <div className={lc.helpItem}>
        <span>Boxes remaining</span>
        <p>
          On hand, less boxes already planned or confirmed onto pallets, less anything on hold. It is the work left, not the stock balance. A group
          can have plenty available and nothing remaining here because it is all already on pallets.
        </p>
      </div>
      <div className={lc.helpItem}>
        <span>Equivalent is an estimate</span>
        <p>
          Remaining boxes divided by the source capacity, rounded up. How many pallets the work is worth if each is filled. Create pallets opens
          with that figure prefilled and you change it there; nothing is created from this row alone.
        </p>
      </div>
      <div className={lc.helpItem}>
        <span>Two ways to palletise</span>
        <p>
          From here, or from an order's allocation. Both open the same Create pallets panel and produce the same planned pallets. The difference is
          only what is filled in for you: from here the source is locked and the order is optional; from an order the source and the order are
          both locked to that reservation.
        </p>
      </div>
      <div className={lc.helpItem}>
        <span>Order allocation is context</span>
        <p>
          It names the orders already reserving stock from this group, so you palletise against real demand first. “No order” is legitimate. Stock
          can be palletised ahead of a sale and assigned later from the pallet itself.
        </p>
      </div>
    </div>
  )
}
